import React, { useRef, useMemo } from 'react';
import { View, StyleSheet, PanResponder, Platform } from 'react-native';
import Svg, { Path as SvgPath } from 'react-native-svg';
import { InputPoint, InputStroke, DefaultInputAdapter } from '@eduwrite/shared';
import { useWorkpadStore } from '../store/useWorkpadStore';

const adapter = new DefaultInputAdapter();

function strokeToSvgPath(stroke: InputStroke): string {
    if (!stroke.points || stroke.points.length === 0) return '';
    const [first, ...rest] = stroke.points;
    const move = `M ${first.x} ${first.y}`;
    const lines = rest.map((p) => `L ${p.x} ${p.y}`).join(' ');
    return `${move} ${lines}`;
}

interface FreeDrawCanvasProps {
    eraserMode?: boolean;
}

export const FreeDrawCanvas: React.FC<FreeDrawCanvasProps> = ({ eraserMode = false }) => {
    const store = useWorkpadStore();
    const { addStroke } = store;
    const currentStrokeRef = useRef<InputStroke | null>(null);
    const [completedStrokes, setCompletedStrokes] = React.useState<InputStroke[]>(store.freeDrawStrokes);
    const [liveStroke, setLiveStroke] = React.useState<InputStroke | null>(null);
    const rAFRef = useRef<number | null>(null);
    const pendingUpdateRef = useRef(false);

    // Keep local strokes in sync with store
    React.useEffect(() => {
        const unsub = useWorkpadStore.subscribe((state) => {
            setCompletedStrokes(state.freeDrawStrokes);
        });
        return unsub;
    }, []);

    const containerRef = useRef<View>(null);
    const layoutRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const flushLiveStroke = React.useCallback(() => {
        rAFRef.current = null;
        pendingUpdateRef.current = false;
        if (currentStrokeRef.current) {
            setLiveStroke({ ...currentStrokeRef.current });
        }
    }, []);

    const scheduleFlush = React.useCallback(() => {
        if (pendingUpdateRef.current) return;
        pendingUpdateRef.current = true;
        rAFRef.current = requestAnimationFrame(flushLiveStroke);
    }, [flushLiveStroke]);

    React.useEffect(() => {
        return () => {
            if (rAFRef.current) {
                cancelAnimationFrame(rAFRef.current);
            }
        };
    }, []);

    const panResponder = useMemo(
        () =>
            PanResponder.create({
                onStartShouldSetPanResponder: () => true,
                onMoveShouldSetPanResponder: () => true,
                onPanResponderGrant: (evt) => {
                    const touch = evt.nativeEvent.touches[0];
                    const point: InputPoint = {
                        x: touch.pageX - layoutRef.current.x,
                        y: touch.pageY - layoutRef.current.y,
                        pressure: (touch as any).force ?? 1,
                        timestamp: Date.now(),
                    };
                    currentStrokeRef.current = { points: [point], toolType: 'pen' };
                    scheduleFlush();
                },
                onPanResponderMove: (evt) => {
                    const touch = evt.nativeEvent.touches[0];
                    if (!touch || !currentStrokeRef.current) return;
                    const point: InputPoint = {
                        x: touch.pageX - layoutRef.current.x,
                        y: touch.pageY - layoutRef.current.y,
                        pressure: (touch as any).force ?? 1,
                        timestamp: Date.now(),
                    };
                    currentStrokeRef.current = {
                        ...currentStrokeRef.current,
                        points: [...currentStrokeRef.current.points, point],
                    };
                    scheduleFlush();
                },
                onPanResponderRelease: () => {
                    if (rAFRef.current) {
                        cancelAnimationFrame(rAFRef.current);
                        rAFRef.current = null;
                    }
                    pendingUpdateRef.current = false;
                    if (currentStrokeRef.current) {
                        let finalStroke = currentStrokeRef.current;
                        try {
                            if (finalStroke.points.length >= 2) {
                                finalStroke = adapter.smoothStroke(finalStroke);
                            }
                        } catch (_e) {
                            // keep raw stroke
                        }
                        addStroke(finalStroke);
                        currentStrokeRef.current = null;
                        setLiveStroke(null);
                    }
                },
                onPanResponderTerminate: () => {
                    if (rAFRef.current) {
                        cancelAnimationFrame(rAFRef.current);
                        rAFRef.current = null;
                    }
                    pendingUpdateRef.current = false;
                    currentStrokeRef.current = null;
                    setLiveStroke(null);
                },
            }),
        [addStroke, scheduleFlush]
    );

    return (
        <View
            ref={containerRef}
            style={[
                styles.container,
                styles.interactiveContainer,
                Platform.OS === 'web' && ({ touchAction: 'none' } as any),
            ]}
            accessibilityLabel="Free draw canvas"
            accessibilityRole="image"
            onLayout={(_e) => {
                containerRef.current?.measure((x, y, w, h, pageX, pageY) => {
                    layoutRef.current = { x: pageX, y: pageY };
                });
            }}
            {...panResponder.panHandlers}
        >
            <Svg width="100%" height="100%" style={StyleSheet.absoluteFill}>
                {completedStrokes.map((stroke, i) => (
                    <SvgPath
                        key={`c-${i}`}
                        d={strokeToSvgPath(stroke)}
                        stroke={eraserMode ? '#F8FAFC' : '#000000'}
                        strokeWidth={eraserMode ? 12 : 3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                    />
                ))}
                {liveStroke && (
                    <SvgPath
                        d={strokeToSvgPath(liveStroke)}
                        stroke={eraserMode ? '#F8FAFC' : '#3B82F6'}
                        strokeWidth={eraserMode ? 12 : 3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                    />
                )}
            </Svg>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    interactiveContainer: {
        backgroundColor: '#F8FAFC',
        borderWidth: 2,
        borderColor: '#CBD5E1',
        borderStyle: 'dashed',
        borderRadius: 12,
        margin: 4,
    },
});
